/* eslint-disable complexity */
import React, { useState } from 'react';

import { LoadingButton } from '@atlaskit/button';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import VidPlayIcon from '@atlaskit/icon/glyph/vid-play';

import { Flex } from '@qality/common-components/src/atoms/Flex';
import { messages } from '@qality/common-messages';
import { TestCaseInCycle } from '@qality/common-model';
import {
  FlagService,
  StorageOfUniqueServices,
  TestCycleService,
} from '@qality/common-service';

import {
  ExecuteButtonStyled,
  MenuOptionIconStyled,
  MenuOptionLabelStyled,
  TestCycleTcListTabItemMenuOptionStyled,
} from './TestCycleTcListTabItemMenu.styled';
import './_test-cycle-list-tab.scss';
import DeleteTestCaseFromCycleDialog from './test-cases/DeleteTestCaseFromCycleDialog';

type Props = {
  testCase: TestCaseInCycle;
  canEdit: boolean;
  removeTestCase: (testCase: TestCaseInCycle) => Promise<void>;
  onExecute: (testCaseInCycle?: TestCaseInCycle) => Promise<void>;
};

export default function TestCycleTcListTabItemMenu({
  testCase,
  canEdit,
  removeTestCase,
  onExecute,
}: Props): JSX.Element {
  const items = generateDropDownList();
  const [isOpen, changeIsOpen] = useState(false);
  const [isExecutionDisabled, setIsExecutionDisabled] = useState(false);

  const handleExecution = async () => {
    try {
      setIsExecutionDisabled(true);
      await onExecute();
    } catch {
      setIsExecutionDisabled(false);
    }
  };

  return (
    <>
      <Flex spaceBetween>
        {canEdit && (
          <ExecuteButtonStyled>
            <LoadingButton
              onClick={handleExecution}
              isDisabled={isExecutionDisabled}
              isLoading={isExecutionDisabled}
            >
              <TestCycleTcListTabItemMenuOptionStyled>
                <MenuOptionIconStyled>
                  <VidPlayIcon
                    size='small'
                    primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                    label='playIcon'
                  />
                </MenuOptionIconStyled>
                <MenuOptionLabelStyled id='qality-test-case-action-execute'>
                  {messages['testCycle.details.list.action.openExecution']}
                </MenuOptionLabelStyled>
              </TestCycleTcListTabItemMenuOptionStyled>
            </LoadingButton>
          </ExecuteButtonStyled>
        )}
        <DropdownMenu
          shouldFlip={false}
          placement='left-start'
          trigger={({ triggerRef, ...props }) => (
            <Button
              {...props}
              iconBefore={
                <MoreIcon
                  size='small'
                  primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                  label='moreIcon'
                />
              }
              ref={triggerRef}
              appearance='default'
            />
          )}
          testId='testCaseDropdown'
        >
          <DropdownItemGroup>{items}</DropdownItemGroup>
        </DropdownMenu>
      </Flex>
      <DeleteTestCaseFromCycleDialog
        isOpen={isOpen}
        onDelete={deleteTestCaseFromCycle}
        onCancel={() => changeIsOpen(false)}
      />
    </>
  );

  function deleteTestCaseFromCycle() {
    return TestCycleService.removeTestCaseFromCycle(
      testCase,
      testCase.testCycle?.id
    )
      .then(() => {
        removeTestCase(testCase);
        FlagService.createSuccessFlag(
          'Success',
          messages['testCycle.details.remove.testCase.from.testCycle.success']
        );
        return;
      })
      .catch((e) => {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['testCycle.details.remove.testCase.from.testCycle.error']
        );
      });
  }

  function generateDropDownList() {
    const itemLists = [];

    if (canEdit) {
      itemLists.push(
        <DropdownItem key='remove-test-case-from-cycle'>
          <div
            className='qality-actions-wrapper'
            onClick={() => {
              changeIsOpen(true);
            }}
          >
            <TestCycleTcListTabItemMenuOptionStyled>
              <MenuOptionIconStyled>
                <TrashIcon
                  size='small'
                  primaryColor='var(--ds-icon, var(--ds-icon))'
                  label='trashIcon'
                />
              </MenuOptionIconStyled>
              <MenuOptionLabelStyled id='qality-test-case-action-delete'>
                {messages['testCycle.details.list.action.delete']}
              </MenuOptionLabelStyled>
            </TestCycleTcListTabItemMenuOptionStyled>
          </div>
        </DropdownItem>
      );
    }

    return itemLists;
  }
}
